import { Controller } from "@hotwired/stimulus"

// Lets the user rearrange the dashboard like an iOS springboard. Widget cards
// (data-widget-key) sit directly in the grid; app tiles (data-app-id) live
// inside full-width band wrappers (data-band) so widgets always start on a
// fresh row. "Customize" enters an edit mode where everything is draggable
// and widgets gain a half/full width toggle, then Save persists the combined
// token order (widget keys and "app_<id>" entries) plus any size changes.
export default class extends Controller {
  static targets = ["grid", "customizeButton", "editActions", "saveButton"]
  static values = { saveUrl: String }

  connect() {
    this.editing = false
    this.dragged = null
    this.sizes = {}
  }

  enterEdit() {
    // Snapshot before adding edit chrome so cancel restores a clean grid.
    this.originalHTML = this.gridTarget.innerHTML
    this.editing = true
    this.sizes = {}
    this.items().forEach(item => item.setAttribute("draggable", "true"))
    this.injectResizeToggles()
    this.gridTarget.classList.add("dashboard-grid--editing")
    this.customizeButtonTarget.hidden = true
    this.editActionsTarget.hidden = false
  }

  exitEdit() {
    this.editing = false
    this.dragged = null
    this.removeResizeToggles()
    this.items().forEach(item => {
      item.removeAttribute("draggable")
      item.classList.remove("dashboard-card--dragging", "dashboard-app-tile--dragging")
    })
    this.gridTarget.classList.remove("dashboard-grid--editing")
    this.customizeButtonTarget.hidden = false
    this.editActionsTarget.hidden = true
  }

  cancel() {
    // Bands get created/merged while dragging, so restore the snapshot
    // wholesale instead of moving nodes back one by one.
    this.gridTarget.innerHTML = this.originalHTML
    this.exitEdit()
  }

  async save() {
    this.saveButtonTarget.disabled = true
    try {
      const response = await fetch(this.saveUrlValue, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        },
        credentials: "same-origin",
        body: JSON.stringify({ order: this.tokens(), sizes: this.sizes })
      })
      if (!response.ok) throw new Error(`Save failed (${response.status})`)
      this.exitEdit()
    } catch (error) {
      console.error("Could not save dashboard order:", error)
      alert("Could not save your dashboard layout. Please try again.")
    } finally {
      this.saveButtonTarget.disabled = false
    }
  }

  // Clicks in edit mode: resize toggles act, app tile links must not navigate.
  gridClick(event) {
    if (!this.editing) return

    const toggle = event.target.closest("[data-resize-toggle]")
    if (toggle) {
      event.preventDefault()
      this.toggleWidgetSize(toggle.closest("[data-widget-key]"))
      return
    }

    if (event.target.closest("[data-app-id]")) event.preventDefault()
  }

  // Widget half/full width toggle

  injectResizeToggles() {
    this.widgets().forEach(card => {
      const button = document.createElement("button")
      button.type = "button"
      button.className = "dashboard-card-resize"
      button.dataset.resizeToggle = "true"
      button.title = "Toggle half / full width"
      button.innerHTML = '<i class="ph ph-arrows-out-line-horizontal"></i>'
      card.appendChild(button)
    })
  }

  removeResizeToggles() {
    this.gridTarget.querySelectorAll("[data-resize-toggle]").forEach(button => button.remove())
  }

  toggleWidgetSize(card) {
    if (!card) return
    // Measure rather than inspect classes: defaults like the always-full
    // academy/workplace cards carry no explicit size class.
    const gridWidth = this.gridTarget.getBoundingClientRect().width
    const isFull = card.getBoundingClientRect().width > gridWidth * 0.75
    card.classList.toggle("dashboard-card--full", !isFull)
    card.classList.toggle("dashboard-card--half", isFull)
    this.sizes[card.dataset.widgetKey] = isFull ? "half" : "full"
  }

  // Drag and drop (events delegated from the grid)

  dragStart(event) {
    if (!this.editing) return
    const item = event.target.closest("[data-widget-key], [data-app-id]")
    if (!item) return
    this.dragged = item
    event.dataTransfer.effectAllowed = "move"
    // Defer the class so the drag image is the full-opacity item.
    requestAnimationFrame(() => item.classList.add(this.draggingClass(item)))
  }

  dragOver(event) {
    if (!this.editing || !this.dragged) return
    event.preventDefault()
    event.dataTransfer.dropEffect = "move"

    if (this.isTile(this.dragged)) {
      this.dragTileOver(event)
    } else {
      this.dragWidgetOver(event)
    }
  }

  dragTileOver(event) {
    const tile = event.target.closest("[data-app-id]")
    if (tile && tile !== this.dragged) {
      const rect = tile.getBoundingClientRect()
      const before = (event.clientX - rect.left) / rect.width < 0.5
      tile.parentElement.insertBefore(this.dragged, before ? tile : tile.nextElementSibling)
      this.cleanupBands()
      return
    }

    const band = event.target.closest("[data-band]")
    if (band && !band.contains(this.dragged)) {
      band.appendChild(this.dragged)
      this.cleanupBands()
      return
    }

    const widget = event.target.closest("[data-widget-key]")
    if (widget) {
      const rect = widget.getBoundingClientRect()
      const before = (event.clientY - rect.top) / rect.height < 0.5
      // Already sitting alone in a band right where it would land? Skip to
      // avoid churning the DOM on every dragover event.
      const neighbour = before ? widget.previousElementSibling : widget.nextElementSibling
      if (neighbour === this.dragged.parentElement && neighbour.children.length === 1) return

      const newBand = this.createBand()
      this.gridTarget.insertBefore(newBand, before ? widget : widget.nextElementSibling)
      newBand.appendChild(this.dragged)
      this.cleanupBands()
    }
  }

  dragWidgetOver(event) {
    // Resolve the drop target to a top-level grid item (widget card or band).
    const target = event.target.closest("[data-widget-key], [data-band]")
    if (!target || target === this.dragged || target.parentElement !== this.gridTarget) return

    const rect = target.getBoundingClientRect()
    const before = (event.clientY - rect.top) / rect.height < 0.5
    this.gridTarget.insertBefore(this.dragged, before ? target : target.nextElementSibling)
    this.cleanupBands()
  }

  drop(event) {
    if (!this.editing) return
    event.preventDefault()
  }

  dragEnd() {
    if (this.dragged) {
      this.dragged.classList.remove("dashboard-card--dragging", "dashboard-app-tile--dragging")
    }
    this.dragged = null
    this.cleanupBands()
  }

  // Band housekeeping: drop empty bands, merge adjacent ones.

  createBand() {
    const band = document.createElement("div")
    band.className = "dashboard-app-band"
    band.dataset.band = "true"
    return band
  }

  cleanupBands() {
    this.bands().forEach(band => {
      if (band.children.length === 0) band.remove()
    })
    this.bands().forEach(band => {
      const next = band.nextElementSibling
      if (next && next.hasAttribute("data-band")) {
        while (next.firstElementChild) band.appendChild(next.firstElementChild)
        next.remove()
      }
    })
  }

  // Helpers

  isTile(item) {
    return item.hasAttribute("data-app-id")
  }

  draggingClass(item) {
    return this.isTile(item) ? "dashboard-app-tile--dragging" : "dashboard-card--dragging"
  }

  bands() {
    return Array.from(this.gridTarget.querySelectorAll("[data-band]"))
  }

  widgets() {
    return Array.from(this.gridTarget.querySelectorAll("[data-widget-key]"))
  }

  items() {
    return Array.from(this.gridTarget.querySelectorAll("[data-widget-key], [data-app-id]"))
  }

  tokens() {
    return this.items().map(item =>
      this.isTile(item) ? `app_${item.dataset.appId}` : item.dataset.widgetKey
    )
  }
}
